/**
 * Component: HeroCarouselCard
 * Author: Dine AI
 * Date: November 4, 2025
 * Last Modified: December 17, 2025 - Light mode conversion
 *
 * Purpose:
 * - Displays the 4 hero carousel items shown to customers in Menu Assistant
 * - Mini auto-rotating carousel preview (matches customer app experience)
 * - Quick access to edit carousel items and their display order
 * - Provides visual feedback on what customers see first
 *
 * Props: None (fetches data via API)
 *
 * State Management:
 * - List of hero carousel items from API
 * - Current carousel index for auto-rotation
 * - Loading state
 *
 * Performance:
 * - Auto-rotation every 4 seconds
 * - Lazy loaded images
 * - Cleanup on unmount
 *
 * Features:
 * - Click item to navigate to edit page
 * - Shows Popular and Chef's Special badges
 * - Empty state if no items configured
 * - Position indicators (1-4)
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  ChefHat,
  Edit,
  ArrowRight,
  Star,
  Trophy
} from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

interface HeroCarouselItem {
  menu_item_id: number;
  item_name: string;
  item_description: string;
  primary_price: number;
  image_url: string;
  hero_display_order: number;
  menu_type: 'regular' | 'seasonal';
  section: string;
  is_popular?: boolean;
  chef_reccom?: boolean;
}

interface HeroCarouselResponse {
  success: boolean;
  hero_items: HeroCarouselItem[];
  count: number;
}

const HeroCarouselCard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [heroItems, setHeroItems] = useState<HeroCarouselItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const attemptFetch = async () => {
      try {
        await fetchHeroItems();
      } catch (error) {
        console.log('[HeroCarouselCard] Initial fetch failed, retrying...');
        setTimeout(() => fetchHeroItems(), 1000);
      }
    };

    attemptFetch();
  }, []);

  // Auto-rotation effect
  useEffect(() => {
    if (heroItems.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroItems.length);
    }, 4000); // Rotate every 4 seconds

    return () => clearInterval(interval);
  }, [heroItems.length]);

  const fetchHeroItems = async () => {
    try {
      console.log('[HeroCarouselCard] Fetching hero carousel items');
      setLoading(true);

      // Wait for auth token if not ready
      const token = Cookies.get('auth_token');
      if (!token) {
        console.log('[HeroCarouselCard] No auth token found, waiting...');
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Get restaurant ID from localStorage (matches RestaurantDashboard pattern)
      const restaurantId = localStorage.getItem('current_rest_id');
      if (!restaurantId) {
        throw new Error('No restaurant selected');
      }

      const response = await apiClient.get<HeroCarouselResponse>(`/menus/${restaurantId}/hero-items`);

      setHeroItems(response.data.hero_items || []);
      console.log('[HeroCarouselCard] Loaded hero items:', response.data.hero_items);
    } catch (error: any) {
      console.error('[HeroCarouselCard] Error fetching hero items:', error);
      if (error.response?.status !== 401) {
        toast({
          title: "Error",
          description: "Failed to load hero carousel items",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    navigate('/hero-carousel/edit');
  };

  const handleItemClick = (itemId: number) => {
    navigate(`/hero-carousel/edit?item=${itemId}`);
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm h-full flex items-center justify-center"
      >
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FDB913]"></div>
          <p className="text-gray-500 text-sm">Loading carousel...</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm h-full flex flex-col"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Menu Assistant Carousel
          </h3>
          <p className="text-gray-500 text-sm">
            These {heroItems.length} item{heroItems.length !== 1 ? 's' : ''} appear first to customers
          </p>
        </div>
        <button
          onClick={handleEditClick}
          className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 hover:text-gray-900 text-sm transition-all"
        >
          <Edit className="h-4 w-4" />
          Edit
        </button>
      </div>

      {/* Carousel Preview or Empty State */}
      {heroItems.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
          <div className="bg-gray-100 rounded-full p-4 mb-3">
            <Sparkles className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm mb-1">No carousel items configured</p>
          <p className="text-gray-400 text-xs mb-4">
            Mark menu items for the carousel in Edit Menu
          </p>
          <button
            onClick={() => navigate('/edit-menu')}
            className="flex items-center gap-2 px-4 py-2 bg-[#FDB913] hover:bg-[#e5a711] rounded-lg text-black font-medium text-sm transition-all"
          >
            Go to Edit Menu
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          {/* Large Carousel Preview */}
          <div className="flex-1 relative bg-gray-100 rounded-xl overflow-hidden mb-3 min-h-[180px] border border-gray-200">
            <AnimatePresence mode="wait">
              {heroItems.map((item, index) => index === currentIndex && (
                <motion.div
                  key={item.menu_item_id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 cursor-pointer"
                  onClick={() => handleItemClick(item.menu_item_id)}
                >
                  {/* Image Background */}
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.item_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <ChefHat className="h-20 w-20 text-gray-400" />
                    </div>
                  )}

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                  {/* Badges */}
                  {(item.is_popular || item.chef_reccom) && (
                    <div className="absolute top-4 right-4 flex gap-2">
                      {item.is_popular && (
                        <span className="px-3 py-1.5 bg-yellow-500 rounded-full text-xs font-bold text-black flex items-center gap-1.5">
                          <Star className="h-3.5 w-3.5" />
                          POPULAR
                        </span>
                      )}
                      {item.chef_reccom && (
                        <span className="px-3 py-1.5 bg-yellow-500 rounded-full text-xs font-bold text-black flex items-center gap-1.5">
                          <ChefHat className="h-3.5 w-3.5" />
                          CHEF'S SPECIAL
                        </span>
                      )}
                    </div>
                  )}

                  {/* Position Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full text-sm font-bold text-white">
                      #{item.hero_display_order}
                    </span>
                  </div>

                  {/* Item Info */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h4 className="text-white font-bold text-xl mb-1 line-clamp-2">
                      {item.item_name}
                    </h4>
                    {item.item_description && (
                      <p className="text-white/80 text-sm mb-2 line-clamp-1">
                        {item.item_description}
                      </p>
                    )}
                    <p className="text-yellow-400 font-bold text-xl">
                      ${item.primary_price.toFixed(2)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Position Indicators */}
          <div className="flex justify-center gap-2">
            {heroItems.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-[#FDB913] w-8'
                    : 'bg-gray-300 hover:bg-gray-400 w-2'
                }`}
                aria-label={`Go to item ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};

export default HeroCarouselCard;
